import { useEffect } from 'react';
import type { ReshuffleServiceRootResponse } from 'src/apis/reshuffle-service/types';
import { isServerError, useAxios } from 'src/hooks/use-axios';
import { NotFound } from 'src/utils/errors';
import { handleErrorWithNotification } from 'src/utils/handle-error';

const cacheKey = 'reshuffle-service';
// TODO: Remove this along with all the !reshuffleService checks below once the reshuffle service is fully deployed to all stages.
const { reshuffleService } = PRISMA_CONFIG;

type ExecuteProps = {
  url: string;
};

export function useReshuffleServiceRoot() {
  const request = useAxios(
    (axios, baseConfig, { url }: ExecuteProps) =>
      axios.request<ReshuffleServiceRootResponse>({
        ...baseConfig,
        url,
      }),
    { neededOnPageLoad: true, cacheKey }
  );

  useEffect(() => {
    if (!reshuffleService) throw new NotFound();
    request.execute({
      url: reshuffleService,
    });
    // oxlint-disable-next-line exhaustive-deps
  }, [reshuffleService]);

  return request;
}

export type ReshuffleServiceRootRequest = ReturnType<
  typeof useReshuffleServiceRoot
>;

export function useOptionalReshuffleServiceRoot() {
  const request = useAxios(
    (axios, baseConfig, { url }: ExecuteProps) =>
      axios.request<ReshuffleServiceRootResponse>({
        ...baseConfig,
        url,
        timeout: 5000,
      }),
    {
      neededOnPageLoad: true,
      cacheKey,
      onError(error) {
        if (reshuffleService && !isServerError(error, 401)) {
          handleErrorWithNotification({
            error,
            setReauthenticate: () => {},
          });
        }
        return error;
      },
    }
  );

  useEffect(() => {
    if (!reshuffleService) return;
    request.execute({
      url: reshuffleService,
    });
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  if (!reshuffleService) return;

  return request;
}

export type OptionalReshuffleServiceRootRequest = ReturnType<
  typeof useOptionalReshuffleServiceRoot
>;
